import { useEffect, useState, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { manewcoTokens, platformToastApiRef, useManewcoTokens } from '@platform/plugin-platform-react';
import type { ManewcoTokens } from '@platform/plugin-platform-react';
import { Snackbar, Box, Typography, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import SyncIcon from '@material-ui/icons/Sync';
import WarningIcon from '@material-ui/icons/Warning';


interface ToastNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: number;
}

const useStyles = makeStyles(theme => {
  const t = manewcoTokens(theme);
  return {
    toast: {
      borderRadius: 10,
      padding: theme.spacing(1.5, 2),
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      minWidth: 320,
      maxWidth: 420,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    },
    toastSuccess: { backgroundColor: t.soft.green, border: `1px solid ${t.vividText.green}` },
    toastError: { backgroundColor: t.soft.red, border: `1px solid ${t.vividText.red}` },
    toastWarning: { backgroundColor: t.soft.amber, border: `1px solid ${t.vividText.amber}` },
    toastInfo: { backgroundColor: t.soft.blue, border: `1px solid ${t.vividText.blue}` },
    toastIcon: { marginTop: 2 },
    toastContent: { flex: 1 },
    toastTitle: { fontWeight: 600, fontSize: '0.85rem' },
    toastMessage: { fontSize: '0.8rem', color: t.toastText, marginTop: 2 },
  };
});

/** Icônes en teintes « texte » : éclaircies en sombre pour rester ≥ 3:1 sur le fond pâle du toast. */
const toastIcon = (type: ToastNotification['type'], vivid: ManewcoTokens['vividText']) =>
  ({
    success: <CheckCircleIcon style={{ color: vivid.green, fontSize: 20 }} />,
    error: <ErrorIcon style={{ color: vivid.red, fontSize: 20 }} />,
    warning: <WarningIcon style={{ color: vivid.amber, fontSize: 20 }} />,
    info: <SyncIcon style={{ color: vivid.blue, fontSize: 20 }} />,
  })[type];

const typeClassMap = {
  success: 'toastSuccess',
  error: 'toastError',
  warning: 'toastWarning',
  info: 'toastInfo',
} as const;


export const ToastHost = () => {
  const classes = useStyles();
  const { vividText } = useManewcoTokens();
  const toastApi = useApi(platformToastApiRef);
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  useEffect(
    () =>
      toastApi.subscribe(toast => {
        setNotifications(prev => [...prev, { ...toast, timestamp: Date.now() }]);
        // Auto-dismiss after 8s
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== toast.id));
        }, 8000);
      }),
    [toastApi],
  );

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Show the latest notification as a snackbar
  const currentNotif = notifications[notifications.length - 1];

  if (!currentNotif) return null;

  const typeClass = typeClassMap[currentNotif.type];

  return (
    <Snackbar
      open={!!currentNotif}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      key={currentNotif.id}
    >
      <Box className={`${classes.toast} ${classes[typeClass]}`}>
        <Box className={classes.toastIcon}>{toastIcon(currentNotif.type, vividText)}</Box>
        <Box className={classes.toastContent}>
          <Typography className={classes.toastTitle}>{currentNotif.title}</Typography>
          <Typography className={classes.toastMessage}>{currentNotif.message}</Typography>
        </Box>
        <IconButton size="small" onClick={() => dismissNotification(currentNotif.id)}>
          <CloseIcon style={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Snackbar>
  );
};
